import React, { useContext }  from 'react'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { Box, Button, FormControl, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useInit } from '../DataFetch/useInit';
import { PostChangePeriod } from '../DataFetch/ManagementAPI';
import { useSuccessDialog } from '../Component/useSuccessDialog';
import { useErrorDialog } from '../Component/useErrorDialog';
import { useConfirmDialog } from '../Component/useConfirmDialog';
import { SessionContext } from '../ManagementRoot';

/**
 * 入力フォームの型
 */
interface FormInputType {
    preEntryStartDate:string,
    preEntryStartHour:string,
    preEntryStartMinute:string,
    preEntryEndDate:string,
    preEntryEndHour:string,
    preEntryEndMinute:string,
    registerStartDate:string,
    registerStartHour:string,
    registerStartMinute:string,
    registerEndDate:string,
    registerEndHour:string,
    registerEndMinute:string,
}

/**
 * @returns 時間Selectオプション
 */
function CreateHourOption () {
    let Hour = [];

    for (let i = 0;i <= 23;i++) {
        let pad = i.toString().padStart(2,"0")
        Hour.push(<MenuItem key={pad} value={pad}>{pad}</MenuItem>)
    }

    return Hour;
}

/**
 * @returns 分Selectオプション
 */
function CreateMinuteOption() {
    let Minute = [];

    for (let i = 0;i <= 59;i++) {
        let pad = i.toString().padStart(2,"0");
        Minute.push(<MenuItem key={pad} value={pad}>{pad}</MenuItem>);
    }

    return Minute;
}

/**
 * 日時文字列から日付を抜き出し、yyyy-mm-dd形式で取得する
 * @param period yyyymmddhhmm形式の日時文字列
 * @returns yyyy-mm-dd形式の日付文字列
 */
function GetPeriodDate(period:string) : string {
    return period.slice(0,4) + "-" + period.slice(4,6) + "-" + period.slice(6,8);
}

/**
 * 日時文字列から時間を抜き出す
 * @param period yyyymmddhhmm形式の日時文字列
 * @returns 時間文字列
 */
function GetPeriodHour(period:string) : string {
    return period.slice(8,10).toString();
}

/**
 * 日時文字列から分を抜き出す
 * @param period yyyymmddhhmm形式の日時文字列
 * @returns 分文字列
 */
function GetPeriodMinute(period:string) : string {
    return period.slice(10,12).toString();
}

/**
 * 日付、時間、分を結合してyyyymmddhhmm形式の文字列にする
 * @param date yyyy-mm-dd形式の日付文字列
 * @param hour 時間文字列
 * @param minute 分文字列
 * @returns yyyymmddhhmm形式の日時文字列
 */
function CombinePeriod(date:string, hour:string, minute:string) {
    return(date.slice(0,4) + date.slice(5,7) + date.slice(8,11) + hour + minute);
}

/**
 * 受付期間変更画面
 */
export function ChangePeriod() {

    const {init} = useInit();

    const {waitConfirm, ConfirmDialog} = useConfirmDialog();

    const {openSuccessDialog, SuccessDialog} = useSuccessDialog();

    const {openErrorDialog, ErrorDialog} = useErrorDialog();

    /**
     * セッション確認関数
     */
    const ConfirmSession = useContext(SessionContext);

    /**
     * 入力フォームHook
     */
    const {control, handleSubmit } = useForm<FormInputType>({
        defaultValues:{
            preEntryStartDate:GetPeriodDate(init.advance_start_period),
            preEntryStartHour:GetPeriodHour(init.advance_start_period),
            preEntryStartMinute:GetPeriodMinute(init.advance_start_period),
            preEntryEndDate:GetPeriodDate(init.advance_end_period),
            preEntryEndHour:GetPeriodHour(init.advance_end_period),
            preEntryEndMinute:GetPeriodMinute(init.advance_end_period),
            registerStartDate:GetPeriodDate(init.register_start_period),
            registerStartHour:GetPeriodHour(init.register_start_period),
            registerStartMinute:GetPeriodMinute(init.register_start_period),
            registerEndDate:GetPeriodDate(init.register_end_period),
            registerEndHour:GetPeriodHour(init.register_end_period),
            registerEndMinute:GetPeriodMinute(init.register_end_period),
        }
    });

    /**
     * 変更処理
     * @param data フォームに入力されたデータ
     */
    async function onSubmit(data:FormInputType) {

        if(!await ConfirmSession()) {
            return
        }

        await waitConfirm("受付期間を変更します。よろしいですか？")
        .then(res => {
            res && PostChangePeriod({
                advanceStartPeriod: CombinePeriod(data.preEntryStartDate,data.preEntryStartHour,data.preEntryStartMinute),
                advanceEndPeriod: CombinePeriod(data.preEntryEndDate,data.preEntryEndHour,data.preEntryEndMinute),
                registerStartPeriod: CombinePeriod(data.registerStartDate,data.registerStartHour,data.registerStartMinute),
                registerEndPeriod: CombinePeriod(data.registerEndDate,data.registerEndHour,data.registerEndMinute),
            })
            .then(res => {
                res ? openSuccessDialog() : openErrorDialog();
            })
        })
        
    }

    /**
     * 一行分の入力フォームを生成
     * @param name 項目名
     * @param registerName 入力フォームHookに渡す識別名
     */
    function PeriodInput(name:string, registerName:"preEntryStart"|"preEntryEnd"|"registerStart"|"registerEnd") {

        return(
            <>
                <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                    <Grid2 xs={12} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"none",sm:"right"}}}>
                            {name}
                        </Typography>                       
                    </Grid2>
                    <Grid2 xs={12} sm="auto">
                        <Controller
                            name={`${registerName}Date`}
                            control={control}
                            rules={{required:"必須項目です。"}}
                            render={({field,fieldState}) => (
                                <TextField type="date" {...field} error={fieldState.invalid}/>
                            )}
                        />
                    </Grid2>
                    <Grid2 xs={6} sm="auto" sx={{textAlign:{xs:"right",sm:"none"}}}>  
                        <Controller
                            name={`${registerName}Hour`}
                            control={control}
                            rules={{required:"必須項目です。"}}
                            render={({field,fieldState}) => (
                                <FormControl error={fieldState.invalid}>
                                    <Select {...field}>
                                        {CreateHourOption()}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid2>
                    <Grid2 xs={6} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"left",sm:"right"}}}>
                            時
                        </Typography>                       
                    </Grid2>
                    <Grid2 xs={6} sm="auto" sx={{textAlign:{xs:"right",sm:"none"}}}>
                        <Controller
                            name={`${registerName}Minute`}
                            control={control}
                            rules={{required:"必須項目です。"}}
                            render={({field,fieldState}) => (
                                <FormControl error={fieldState.invalid}>
                                    <Select {...field}>
                                        {CreateMinuteOption()}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid2>
                    <Grid2 xs={6} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"left",sm:"right"}}}>
                            分
                        </Typography>                       
                    </Grid2>
                </Grid2>   
            </>
        );
    }

    return(
        <>
            <Typography
                variant="h6"
            >
                受付期間変更
            </Typography>
            <Grid2 xs={12}>
                <Paper>
                    <Box m={2} p={1}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box m={1}>
                                {PeriodInput("事前登録開始","preEntryStart")}
                                {PeriodInput("事前登録終了","preEntryEnd")}
                                {PeriodInput("本登録開始　","registerStart")}
                                {PeriodInput("本登録終了　","registerEnd")}
                            </Box>
                            <Box m={1}>
                                <Button type="submit" variant="contained">変更</Button>
                            </Box>
                            
                        </form>
                    </Box>
                </Paper>
            </Grid2>

            <ConfirmDialog/>
            <SuccessDialog/>
            <ErrorDialog/>
        </>
    );
}